import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowRightLeft, ArrowLeft, ArrowRight, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { OrganisationPicker } from '../components/OrganisationPicker';
import { ModeSelector } from '../components/ModeSelector';
import { MigrationPreview } from '../components/MigrationPreview';
import { ConfirmMigrationDialog } from '../components/ConfirmMigrationDialog';
import { MigrationProgress } from '../components/MigrationProgress';
import { MigrationResult } from '../components/MigrationResult';
import { useMigrationState } from '../hooks/useMigrationState';
import { previewMigration, validateMigration, executeMigration, cleanupMigration } from '../api';
import type { MigrationMode } from '../types';

interface PreviewParams { sourceId: string; targetId: string; mode: MigrationMode }
interface ExecuteParams { sourceId: string; targetId: string; mode: MigrationMode; archiveSource: boolean }

export function AccountMigrationPage() {
  const { state, actions } = useMigrationState();

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: (params: PreviewParams) =>
      previewMigration(params.sourceId, params.targetId, params.mode),
    onSuccess: (data) => {
      actions.previewSuccess(data);
      toast.success('Preview loaded successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to load preview');
    },
  });

  // Validate mutation
  const validateMutation = useMutation({
    mutationFn: (params: PreviewParams) =>
      validateMigration(params.sourceId, params.targetId, params.mode),
    onSuccess: (data) => {
      if (data.valid) {
        actions.validateSuccess(data);
        toast.success('Validation passed — no conflicts found');
      } else {
        actions.validateFailure(data);
        toast.warning(`Validation found ${data.conflicts.length} conflict(s)`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Validation failed');
    },
  });

  // Execute mutation
  const executeMutation = useMutation({
    mutationFn: (params: ExecuteParams) =>
      executeMigration(params.sourceId, params.targetId, params.mode, params.archiveSource),
    onMutate: () => {
      actions.executeStart();
    },
    onSuccess: (response) => {
      if (response.status) {
        actions.executeSuccess(response);
        toast.success('Migration completed successfully');
      } else {
        actions.executeFailure(response);
        toast.error('Migration completed with errors');
      }
    },
    onError: (error: Error, params: ExecuteParams) => {
      actions.executeFailure({
        status: false,
        mode: params.mode,
        results: [],
        sourceArchived: false,
        totalMigrated: 0,
        errors: [error.message || 'Migration failed'],
        duration: 0,
      });
      toast.error('Migration failed');
    },
  });

  // Cleanup mutation
  const cleanupMutation = useMutation({
    mutationFn: (batchId: string) => cleanupMigration(batchId),
    onSuccess: (response) => {
      toast.success(`Cleanup complete: ${response.totalDeleted} document(s) deleted`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Cleanup failed');
    },
  });

  const handleCleanup = () => {
    if (state.batchId) {
      cleanupMutation.mutate(state.batchId);
    }
  };

  const executeGuardRef = useRef(false);

  const handleBackToSelectAccounts = () => {
    previewMutation.reset();
    validateMutation.reset();
    actions.backToSelectAccounts();
  };

  const handleReset = () => {
    executeGuardRef.current = false;
    previewMutation.reset();
    validateMutation.reset();
    cleanupMutation.reset();
    actions.reset();
  };

  const canProceedFromAccounts =
    state.sourceOrganisationId &&
    state.targetOrganisationId &&
    state.sourceOrganisationId !== state.targetOrganisationId;

  const handleNextFromAccounts = () => {
    if (canProceedFromAccounts) {
      actions.nextToMode();
    }
  };

  const handleNextFromMode = () => {
    if (!state.sourceOrganisationId || !state.targetOrganisationId) return;
    previewMutation.mutate({
      sourceId: state.sourceOrganisationId,
      targetId: state.targetOrganisationId,
      mode: state.mode,
    });
  };

  const handleValidate = () => {
    if (!state.sourceOrganisationId || !state.targetOrganisationId) return;
    validateMutation.mutate({
      sourceId: state.sourceOrganisationId,
      targetId: state.targetOrganisationId,
      mode: state.mode,
    });
  };

  const handleExecute = () => {
    if (executeGuardRef.current || executeMutation.isPending) return;
    if (!state.sourceOrganisationId || !state.targetOrganisationId) return;
    executeGuardRef.current = true;
    executeMutation.mutate({
      sourceId: state.sourceOrganisationId,
      targetId: state.targetOrganisationId,
      mode: state.mode,
      archiveSource: state.mode === 'copy' ? false : state.archiveSource,
    });
  };

  const isExecuting = executeMutation.isPending;
  const validationPassed = state.validationData?.valid === true;

  useEffect(() => {
    if (state.step !== 'executing') return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state.step]);

  return (
    <div className="space-y-6">
      <Card className="border-destructive bg-destructive/10">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-7 w-7 text-destructive shrink-0" />
            <p className="text-lg font-bold text-destructive">
              Still in active development. Do not use on real accounts.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <ArrowRightLeft className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">Account Migration</h1>
        </div>
        <p className="text-muted-foreground mt-1">
          {state.mode === 'copy'
            ? 'Copy configuration between customer accounts.'
            : 'Move assets between customer accounts.'}
        </p>
      </div>

      {/* Step: Select Accounts */}
      {state.step === 'select-accounts' && (
        <>
          <OrganisationPicker
            sourceId={state.sourceOrganisationId}
            targetId={state.targetOrganisationId}
            onSelectSource={actions.selectSource}
            onSelectTarget={actions.selectTarget}
          />
          <div className="flex justify-end">
            <Button
              onClick={handleNextFromAccounts}
              disabled={!canProceedFromAccounts}
            >
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </>
      )}

      {/* Step: Select Mode */}
      {state.step === 'select-mode' && (
        <>
          <ModeSelector
            selectedMode={state.mode}
            onSelectMode={actions.selectMode}
          />
          <div className="flex justify-between">
            <Button variant="outline" onClick={handleBackToSelectAccounts}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button
              onClick={handleNextFromMode}
              disabled={previewMutation.isPending}
            >
              {previewMutation.isPending ? 'Loading preview...' : 'Next'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </>
      )}

      {/* Step: Preview */}
      {state.step === 'preview' && state.previewData && (
        <>
          <MigrationPreview
            previewData={state.previewData}
            archiveSource={state.archiveSource}
            onSetArchive={actions.setArchive}
            mode={state.mode}
          />

          {/* Validation results */}
          {state.validationData && !state.validationData?.valid && (
            <Card className="border-destructive/50">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  <span className="text-sm font-medium text-destructive">Validation Conflicts</span>
                </div>
                {state.validationData.conflicts.map((conflict, i) => (
                  <div key={i} className="mb-2">
                    <p className="text-sm text-destructive">{conflict.message}</p>
                    {conflict.items.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {conflict.items.map((item) => (
                          <Badge key={item._id} variant="outline" className="text-xs text-destructive">
                            {item.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {state.validationData?.valid && (
            <Card className="border-green-200">
              <CardContent className="pt-4">
                <p className="text-sm text-green-600">No conflicts found. Safe to proceed.</p>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={handleReset}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Start Over
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleValidate}
                disabled={validateMutation.isPending}
              >
                {validateMutation.isPending ? 'Validating...' : 'Validate'}
              </Button>
              <Button
                onClick={actions.confirm}
                disabled={!validationPassed}
              >
                Execute Migration
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Step: Confirm */}
      {state.step === 'confirm' && state.previewData && (
        <>
          <MigrationPreview
            previewData={state.previewData}
            archiveSource={state.archiveSource}
            onSetArchive={actions.setArchive}
            mode={state.mode}
          />
          <ConfirmMigrationDialog
            open={true}
            onOpenChange={(open) => {
              if (!open) actions.backToPreview();
            }}
            sourceName={state.sourceOrganisationName || ''}
            targetName={state.targetOrganisationName || ''}
            totalDocuments={state.previewData.totalDocuments}
            mode={state.mode}
            onConfirm={handleExecute}
            isExecuting={isExecuting}
            databaseEnvironment={state.previewData.databaseEnvironment}
          />
        </>
      )}

      {/* Step: Executing */}
      {state.step === 'executing' && (
        <MigrationProgress isExecuting={true} />
      )}

      {/* Step: Success / Error */}
      {(state.step === 'success' || state.step === 'error') && state.executionResult && (
        <MigrationResult
          result={state.executionResult}
          onReset={handleReset}
          onCleanup={state.batchId ? handleCleanup : undefined}
          isCleaningUp={cleanupMutation.isPending}
          cleanupDone={cleanupMutation.isSuccess}
        />
      )}
    </div>
  );
}
